// BCM-898 · Goods Receipt Dashboard — shared QA release card (PA10 / PA11).
// Matches the mockup (slides 31/32): a header + a single pink card with horizontal
// labelled fields and a "Sync SAP QA Check Interface" button.
// Read-only from A1 (batchQty, scheduleAgreementNumber, vendorBatchNumber).
//
// "QA Release Status in SAP" is HARDCODED from the pending action — the read does
// not carry a per-batch SAP QA status, and the action itself already tells us the
// state: PA10 => "Pending (Quality Inspection)", PA11 => "Rejected (Blocked Stock)".
//
// The Sync SAP QA Check Interface button fires exactly ONE API — resetWorkflowPostGrn
// (batchResetFor: "QA SAP Sync") — which pulls the SAP QA decision and resets the
// post-GRN workflow. On success the user is pointed at Refresh to reload the batch.
import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Button, CircularProgress } from "@material-ui/core";
import SectionShell from "../SectionShell";
import FieldCard from "./FieldCard";
import { useGRStyles } from "../../goodsReceiptStyles";
import { presentFormatedData } from "../../../functionalComponents/dataFormatFunctions";
import { resetWorkflowPostGrn } from "../../api/goodsReceiptApi";
import { useGRFeedback } from "../../context/GRFeedbackContext";
import {
  BATCH_RESET_FOR_QA_SAP_SYNC,
  QA_STATUS_PENDING_TEXT,
  QA_STATUS_REJECTED_TEXT,
  GENERIC_ERROR,
} from "../../utils/constants";

// onRefresh — parent's dashboard refetch (threaded through PA10/PA11 →
// ActionSection). Wired here so the success flow uses the shared
// GRFeedbackContext (toast + lightweight status poll + blocking backdrop),
// identical to every other status-changing action on this dashboard.
const QAReleaseCard = ({ row, title, subText, rejected, viewOnly, onRefresh }) => {
  const classes = useGRStyles();
  const feedback = useGRFeedback();
  const [running, setRunning] = useState(false);
  const cancelRef = useRef(null);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (cancelRef.current) cancelRef.current.cancel("leaving QA release card");
    };
  }, []);

  // The QA Release Status in SAP is hardcoded from the action state (see header).
  const qaStatusText = rejected ? QA_STATUS_REJECTED_TEXT : QA_STATUS_PENDING_TEXT;

  // Exactly ONE API on click — resetWorkflowPostGrn with batchResetFor "QA SAP Sync".
  // vendorBatchNumber is the sponsor/produced batch number on this row (the workflow
  // keys process-flow-{poNumber}-{sponsorBatchNumber} off it); cmoBatchNumber is the
  // CMO's own batch number, falling back to vendorBatchNumber when not distinct.
  const onSyncQA = () => {
    if (running) return;
    setRunning(true);
    const source = axios.CancelToken.source();
    cancelRef.current = source;
    resetWorkflowPostGrn(
      {
        poNumber: row.scheduleAgreementNumber,
        sponsorBatchNumber: row.vendorBatchNumber,
        batchNumber: row.cmoBatchNumber || row.vendorBatchNumber,
        vendorMosymphonyID: row.vendorMosymphonyID,
        schrockenDeliveryScheduleBatchID: row.schrockenDeliveryScheduleBatchID,
        batchResetFor: BATCH_RESET_FOR_QA_SAP_SYNC,
      },
      source.token
    )
      .then((res) => {
        if (!mountedRef.current) return;
        const ok = !!(res && res.data && res.data.status);
        if (ok) {
          feedback.notifySuccess({
            message: "QA sync triggered. Refreshing the batch…",
            statusChanging: true,
            // resetWorkflowPostGrn re-runs the post-GRN workflow.
            resetsBatch: true,
            batchId: row.schrockenDeliveryScheduleBatchID,
            prevStatus: row.SASLBatchStatusID,
            refetch: onRefresh,
            actionLabel: "Sync SAP QA Check Interface",
          });
        } else {
          feedback.notifyError((res && res.data && res.data.message) || GENERIC_ERROR);
        }
      })
      .catch((err) => {
        if (axios.isCancel(err)) return;
        if (!mountedRef.current) return;
        feedback.notifyError(
          (err.response && err.response.data && err.response.data.message) || GENERIC_ERROR
        );
      })
      .finally(() => {
        if (mountedRef.current) setRunning(false);
      });
  };

  return (
    <SectionShell warn title={title} subText={subText} viewOnly={viewOnly}>
      <FieldCard
        fields={[
          { label: "Produced Batch Number", value: presentFormatedData(row.vendorBatchNumber) },
          { label: "QA Release Status in SAP", value: qaStatusText },
          { label: "Batch Qty", value: `${presentFormatedData(row.batchQty)} ${row.batchUOM || ""}` },
          { label: "PO Number", value: presentFormatedData(row.scheduleAgreementNumber) },
        ]}
        rightAction={
          !viewOnly ? (
            <Button
              className={classes.blueBtn}
              disableElevation
              disabled={running}
              onClick={onSyncQA}
              startIcon={running ? <CircularProgress size={13} color="inherit" /> : null}
            >
              Sync SAP QA Check Interface
            </Button>
          ) : null
        }
      />
    </SectionShell>
  );
};

export default QAReleaseCard;
